// The Avenue School/the-avenue-school/src/components/Enrolments.js
import React, { useState } from 'react';
import axios from 'axios';
import { Container, Row, Col, Card, Form, Button, Alert, ListGroup, Modal } from 'react-bootstrap';
import PdfCanvasViewer from './PdfCanvasViewer';
import { API_BASE } from '../lib/apiBase';
import PageHeader from './PageHeader';
import '../styles/Enrolments.css';

/**
 * Enrolments screen:
 *  - PageHeader (top)
 *  - Figma content block (intro card, images, two-column text + resources)
 *  - Existing enquiry form (bottom)
 *  - PDF viewer modal for resources
 */

const Enrolments = () => {
  // Local form state mirrors the three fields required by the backend schema
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
const [loading, setLoading] = useState(false);
const [errorMsg, setErrorMsg] = useState('');

const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitted(false);
  setErrorMsg('');
  setLoading(true);

  try {
    const res = await axios.post(
      `${API_BASE}/enrolments`,
      { name, email, message },
      { withCredentials: true, timeout: 15000 } // add timeout so it can't hang forever
    );

    if (res.status >= 200 && res.status < 300) {
      setSubmitted(true);
      setName('');
      setEmail('');
      setMessage('');
    } else {
      setErrorMsg(`Unexpected status: ${res.status}`);
    }
  } catch (err) {
    console.error('❌ Enrolment submit failed:', err);
    setErrorMsg(
      err?.response?.data?.error ||
      err?.message ||
      'Submission failed'
    );
  } finally {
    setLoading(false);
  }
};

  const enrolmentDocuments = [
    { name: 'Information Handbook', file: '/enrolments/Parent and Student Handbook.pdf' },
    { name: 'Enrolment Policy', file: '/enrolments/Enrolment Policy.pdf' },
    { name: 'Privacy Collection Statement', file: '/enrolments/Privacy Collection Statement.pdf' },
    { name: 'Referral Form', file: '/enrolments/Enrolment Referral Form.pdf' },
    { name: 'Enrolment Agreement', file: '/enrolments/Enrolment Agreement.pdf' }
  ];

  const [showViewer, setShowViewer] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null); // { name, file }

  // Document filenames contain spaces, so always URL-encode the tail segment
  const buildEncodedUrl = (path) => {
    if (!path) return '';
    const segments = path.split('/');
    const filename = segments.pop();
    const base = segments.join('/') || '';
    return `${base}/${encodeURIComponent(filename)}`;
  };

  // HEAD request verifies the asset exists before opening the preview modal
  const handleOpen = async (doc) => {
    const url = buildEncodedUrl(doc.file);
    try {
      const res = await fetch(url, { method: 'HEAD' });
      if (!res.ok) throw new Error('Not found');
    } catch (e) {
      // Some hosts don’t support HEAD; still try inline
    }
    setActiveDoc(doc);
    setShowViewer(true);
  };

  const handleClose = () => {
    setShowViewer(false);
    setTimeout(() => setActiveDoc(null), 150);
  };

  return (
    <div className="enrolments">
      {/* ===== Page Header (top) ===== */}
      <PageHeader eyebrow="THE AVENUE SCHOOL" title="Enrolments"         image="/media/gallery-3.jpg"
        height="200px"/>

      {/* ===== Figma content (middle) ===== */}
      <div className="enrolments__wrap">
        {/* Intro card */}
        <Container className="enrolments__introContainer">
          <div className="enrolments__introCard">
            <div className="enrolments__introHeader">
              <div className="enrolments__eyebrow">THE AVENUE SCHOOL</div>
              <div className="enrolments__title">Enrolments</div>
              <div className="enrolments__lead">
                The Avenue School offers a unique enrolment opportunity for students aged 15–20 who are eligible for Senior Secondary Education but have faced challenges in traditional school settings. We welcome young people who have experienced school refusal, irregular attendance, or are at risk of disengaging.
                <br /><br />
                Our flexible, trauma-informed approach supports students with diagnosed anxiety or mental health-related learning barriers. Priority is given to students from the Cottage Program at Mountain District Learning Centre. With small class sizes and a personalised learning model, we help students re-engage with education and build confidence in a safe, inclusive environment.
              </div>
            </div>

            {/* Two bordered images */}
            <Row className="g-4 enrolments__imageRow">
              <Col md={6}>
                <figure className="enrolments__imageFrame enrolments__imageFrame--left">
                  <img
                    src="/media/gallery-6.jpg"
                    alt=""
                    loading="lazy"
                  />
                </figure>
              </Col>
              <Col md={6}>
                <figure className="enrolments__imageFrame enrolments__imageFrame--right">
                  <img
                    src="/media/gallery-5.jpg"
                    alt=""
                    loading="lazy"
                  />
                </figure>
              </Col>
            </Row>
          </div>
        </Container>

        {/* Two-column section: Who Can Enrol + Downloadable Resources */}
        <Container className="enrolments__twoColContainer">
          <Row className="g-5 align-items-start">
            <Col md={6}>
              <section className="enrolments__col" aria-labelledby="who-can-enrol">
                <h2 id="who-can-enrol" className="enrolments__sectionTitle">Who Can Enrol</h2>
                <p className="enrolments__body">
                  We support students aged 15–20 who haven’t completed a Senior Secondary Certificate and are motivated to pursue their education.
                  <br /><br />
                  Our program is designed for those who benefit from a flexible, alternative approach—especially students with anxiety, mental health challenges, or a history of school refusal.
                  <br /><br />
                  Priority is given to Cottage Program students and those with diagnosed learning barriers. Please note: class caps apply and waiting lists are maintained.
                </p>
              </section>
            </Col>

            <Col md={6}>
              <section
            className="enrolments__col"
            aria-labelledby="downloadable-resources-heading"
          >
            <h2 id="downloadable-resources-heading" className="enrolments__sectionTitle">
              Downloadable Resources
            </h2>
              <p className="enrolments__body">
                You can download all key enrolment documents here to help guide your application process:
                <br />
                <strong>
                  <a href={buildEncodedUrl("/enrolments/Parent and Student Handbook.pdf")} target="_blank" rel="noopener">Information Handbook</a><br />
                  <a href={buildEncodedUrl("/enrolments/Enrolment Policy.pdf")} target="_blank" rel="noopener">Enrolment Policy</a><br />
                  <a href={buildEncodedUrl("/enrolments/Privacy Collection Statement.pdf")} target="_blank" rel="noopener">Privacy Collection Statement</a><br />
                  <a href={buildEncodedUrl("/enrolments/Enrolment Referral Form.pdf")} target="_blank" rel="noopener">Referral Form</a><br />
                  <a href={buildEncodedUrl("/enrolments/Enrolment Agreement.pdf")} target="_blank" rel="noopener">Enrolment Agreement</a>
                </strong>
                <br />
                These resources provide essential information about our school, policies, and expectations.
                If you need help completing your application, please contact our team.
              </p>
            
          </section>
            </Col>
          </Row>
        </Container>
      </div>

      {/* ===== Existing Enquiry form (bottom) — unchanged structure, styled via CSS ===== */}
      {/* ===== Enquiry form (Figma styles) ===== */}
<section className="enrolmentsForm">
  <Container>
    <Row className="justify-content-center">
      <Col lg={10} xl={8}>
        <header className="enrolmentsForm__header">
          <h2 className="enrolmentsForm__heading">Start Your Journey Today</h2>
          <p className="enrolmentsForm__subheading">
            Have questions or ready to apply? Use the form below to contact us—we’re here to support you every step of the way.
          </p>
        </header>
      </Col>
    </Row>

    <Row className="justify-content-center">
      <Col lg={8}>
        <div className="enrolmentsForm__card shadow-sm">
          <Form onSubmit={handleSubmit} className="enrolmentsForm__form">
            <Form.Group className="mb-3">
              <Form.Label className="sr-only" htmlFor="enrol-name">Name</Form.Label>
              <Form.Control
                id="enrol-name"
                className="enrolmentsForm__control"
                type="text"
                placeholder="Name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="sr-only" htmlFor="enrol-email">Email</Form.Label>
              <Form.Control
                id="enrol-email"
                className="enrolmentsForm__control"
                type="email"
                placeholder="Email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="sr-only" htmlFor="enrol-message">Message</Form.Label>
              <Form.Control
                id="enrol-message"
                className="enrolmentsForm__textarea"
                as="textarea"
                rows={6}
                placeholder="Please type your message here..."
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </Form.Group>

            <Button type="submit" className="enrolmentsForm__submit w-100" disabled={loading}>
  {loading ? 'Sending…' : 'Send message'}
</Button>

{submitted && (
  <Alert variant="success" className="mt-3 text-center">
    Thank you! Your message has been sent.
  </Alert>
)}
{errorMsg && (
  <Alert variant="danger" className="mt-3 text-center">
    {errorMsg}
  </Alert>
)}
          </Form>
        </div>
      </Col>
    </Row>
  </Container>
</section>


      {/* ===== PDF viewer modal ===== */}
      <Modal
        show={showViewer}
        onHide={handleClose}
        size="xl"
        centered
        dialogClassName="pdf-viewer-modal"
        aria-labelledby="enrolment-pdf-viewer-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="enrolment-pdf-viewer-title">{activeDoc?.name ?? 'Document'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="enrolments__pdfBody">
          {activeDoc ? (
            <div className="enrolments__pdfViewport">
              <PdfCanvasViewer fileUrl={buildEncodedUrl(activeDoc.file)} />
            </div>
          ) : (
            <div className="p-4">No document selected.</div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <small className="text-muted">
            Use your browser’s PDF toolbar to zoom/print. Scroll to read all pages.
          </small>
          {activeDoc && (
            <a className="btn btn-primary fw-semibold" href={buildEncodedUrl(activeDoc.file)} download>
              Download
            </a>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Enrolments;



